import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { AlertTriangle } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Plus, FileText, Clock, CheckCircle, AlertCircle, FileEdit, Lock, Download, ChevronLeft, ChevronRight, Mail, X } from "lucide-react";
import { generateInvoicePdf } from "@/lib/generateInvoicePdf";
import { toast } from "sonner";
import { useTrialLimit } from "@/hooks/useTrialLimit";
import { useUserTier } from "@/hooks/useUserTier";
import AppHeader from "@/components/AppHeader";
import AppFooter from "@/components/AppFooter";
import DeletionBanner from "@/components/DeletionBanner";
import { useEffectiveBetaMode } from "@/hooks/useEffectiveBetaMode";
import AccountantSendDialog from "@/components/AccountantSendDialog";
import { Checkbox } from "@/components/ui/checkbox";

const PAGE_SIZE = 20;

const statusConfig: Record<string, { label: string; icon: any; variant: "default" | "secondary" | "destructive" | "outline" | "success" | "warning" | "info" }> = {
  draft: { label: "Ciornă", icon: FileEdit, variant: "secondary" },
  pending: { label: "În așteptare", icon: Clock, variant: "warning" },
  validated: { label: "Validată", icon: CheckCircle, variant: "success" },
  error: { label: "Eroare", icon: AlertCircle, variant: "destructive" },
};

const Dashboard = () => {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const { effectiveBetaMode: BETA_MODE } = useEffectiveBetaMode();
  const { hasReachedLimit, remaining, limit, isUnlimited } = useTrialLimit();
  const { tierName, hasTier, isLoading: tierLoading } = useUserTier();
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [acctDialogOpen, setAcctDialogOpen] = useState(false);

  const toggleSelect = (id: string, e?: React.MouseEvent) => {
    e?.stopPropagation();
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const clearSelection = () => setSelectedIds(new Set());

  // Reset to page 0 when filter changes
  useEffect(() => { setPage(0); }, [statusFilter]);

  const handleDownloadPdf = useCallback(async (e: React.MouseEvent, inv: any) => {
    e.stopPropagation();
    try {
      const { data: items, error } = await supabase
        .from("invoice_items")
        .select("*")
        .eq("invoice_id", inv.id)
        .order("sort_order");
      if (error) throw error;
      const doc = generateInvoicePdf({
        invoice_number: inv.invoice_number,
        issue_date: inv.issue_date,
        due_date: inv.due_date,
        seller_name: inv.seller_name,
        seller_cui: inv.seller_cui,
        seller_address: inv.seller_address,
        seller_city: inv.seller_city,
        seller_county: inv.seller_county,
        buyer_name: inv.buyer_name,
        buyer_cui: inv.buyer_cui,
        buyer_address: inv.buyer_address,
        buyer_city: inv.buyer_city,
        buyer_county: inv.buyer_county,
        subtotal: Number(inv.subtotal),
        vat_total: Number(inv.vat_total),
        total: Number(inv.total),
        items: (items || []).map((i) => ({
          item_name: i.item_name,
          quantity: Number(i.quantity),
          unit_price: Number(i.unit_price),
          vat_rate: Number(i.vat_rate),
          unit_code: i.unit_code || "C62",
        })),
      });
      doc.save(`factura-${inv.invoice_number || inv.id.slice(0, 8)}.pdf`);
      toast.success("PDF descărcat!");
    } catch (err) {
      console.error("PDF generation error:", err);
      toast.error("Eroare la generarea PDF-ului");
    }
  }, []);

  useEffect(() => {
    if (!loading && !user) navigate("/auth");
    if (!loading && user && !tierLoading && !hasTier) navigate("/select-plan");
  }, [user, loading, navigate, tierLoading, hasTier]);

  // Fetch total count for stats (all invoices)
  const { data: allInvoices } = useQuery({
    queryKey: ["invoices-stats", user?.id],
    enabled: !!user,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("invoices")
        .select("id, status")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data;
    },
  });

  // Fetch paginated invoices
  const { data: paginatedResult, isLoading } = useQuery({
    queryKey: ["invoices-page", user?.id, statusFilter, page],
    enabled: !!user,
    queryFn: async () => {
      let query = supabase
        .from("invoices")
        .select("*", { count: "exact" })
        .order("created_at", { ascending: false });

      if (statusFilter) {
        query = query.eq("status", statusFilter as "draft" | "pending" | "validated" | "error");
      }

      const from = page * PAGE_SIZE;
      const to = from + PAGE_SIZE - 1;
      query = query.range(from, to);

      const { data, error, count } = await query;
      if (error) throw error;
      return { data: data ?? [], count: count ?? 0 };
    },
  });

  const invoices = paginatedResult?.data ?? [];
  const totalCount = paginatedResult?.count ?? 0;
  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  // Stats from all invoices
  const totalInvoices = allInvoices?.length ?? 0;
  const draftCount = allInvoices?.filter(i => i.status === "draft").length ?? 0;
  const pendingCount = allInvoices?.filter(i => i.status === "pending").length ?? 0;
  const validatedCount = allInvoices?.filter(i => i.status === "validated").length ?? 0;
  const errorCount = allInvoices?.filter(i => i.status === "error").length ?? 0;

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="h-8 w-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <AppHeader />
      <DeletionBanner />

      <main className="container py-6 max-w-2xl">
          {/* Downgrade info banner */}
          {!isUnlimited && limit !== null && totalInvoices > limit && !hasReachedLimit && (
            <div className="rounded-xl border border-primary/20 bg-primary/5 p-5 mb-6 space-y-2">
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-primary shrink-0" />
                <span className="font-heading font-semibold text-sm">Ai mai multe facturi decât permite planul tău actual</span>
              </div>
              <p className="text-xs text-muted-foreground leading-relaxed">
                Planul <span className="font-semibold text-foreground">{tierName}</span> include {limit} facturi/lună.
                Toate facturile tale existente rămân accesibile — le poți <span className="font-semibold text-foreground">vizualiza</span>, <span className="font-semibold text-foreground">edita</span>, <span className="font-semibold text-foreground">descărca</span> ca PDF/XML și <span className="font-semibold text-foreground">trimite la ANAF</span> oricând.
                Crearea de facturi noi este limitată la {limit}/lună.
              </p>
              <Button size="sm" variant="outline" className="text-xs" onClick={() => navigate("/select-plan")}>
                Vezi Planurile
              </Button>
            </div>
          )}

          {/* Trial limit banner */}
          {hasReachedLimit ? (
            <div className="rounded-xl border border-destructive/30 bg-destructive/5 p-5 mb-8 text-center space-y-3">
              <div className="flex items-center justify-center gap-2">
                <Lock className="h-5 w-5 text-destructive" />
                <span className="font-heading font-bold">Limită Atinsă</span>
              </div>
               <p className="text-sm text-muted-foreground">
                 Ai folosit toate cele <span className="font-semibold text-foreground">{limit}</span> facturi din planul tău luna aceasta.
                 {BETA_MODE
                   ? " În perioada beta, planurile plătite nu sunt disponibile. Limita se resetează luna viitoare."
                   : " Fă upgrade pentru a continua."}
              </p>
              {!BETA_MODE && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button className="font-semibold" onClick={() => navigate("/select-plan")}>
                      Fă Upgrade
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Alege un plan cu mai multe facturi</TooltipContent>
                </Tooltip>
              )}
            </div>
          ) : (
            <>
              {!isUnlimited && remaining <= 3 && remaining > 0 && (
                <div className="rounded-lg border border-primary/30 bg-primary/5 px-4 py-3 text-sm text-center mb-4">
                  Mai ai <span className="font-bold text-primary">{remaining}</span> {remaining === 1 ? "factură" : "facturi"} rămase luna aceasta din planul {tierName || "tău"}.
                </div>
              )}
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    size="lg"
                    className="w-full h-14 text-base font-semibold mb-8"
                    onClick={() => navigate("/invoice/new")}
                  >
                    <Plus className="mr-2 h-5 w-5" />
                    Creează Factură Nouă
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Deschide formularul pentru o factură nouă</TooltipContent>
              </Tooltip>
            </>
          )}

          {/* Stats / Filters */}
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-8">
            {[
              { label: "Total", value: totalInvoices, filter: null },
              { label: "Ciorne", value: draftCount, filter: "draft" },
              { label: "În așteptare", value: pendingCount, filter: "pending" },
              { label: "Validate", value: validatedCount, filter: "validated" },
              { label: "Erori", value: errorCount, filter: "error" },
            ].map((s, i) => (
              <Tooltip key={i}>
                <TooltipTrigger asChild>
                  <button
                    onClick={() => setStatusFilter(statusFilter === s.filter ? null : s.filter)}
                    className={`rounded-xl border p-4 text-center transition-colors cursor-pointer ${
                      statusFilter === s.filter
                        ? "border-primary bg-primary/10 ring-1 ring-primary/30"
                        : "border-border bg-card hover:border-primary/30"
                    }`}
                  >
                    <div className="text-2xl font-bold font-heading">{s.value}</div>
                    <div className="text-xs text-muted-foreground mt-1">{s.label}</div>
                  </button>
                </TooltipTrigger>
                <TooltipContent>
                  {statusFilter === s.filter ? "Arată toate facturile" : `Filtrează doar ${s.label.toLowerCase()}`}
                </TooltipContent>
              </Tooltip>
            ))}
          </div>

          {/* Invoice List */}
          <h2 className="font-heading text-lg font-semibold mb-4">
            {statusFilter === "draft" ? "Ciorne" : statusFilter === "pending" ? "Facturi În Așteptare" : statusFilter === "validated" ? "Facturi Validate" : statusFilter === "error" ? "Facturi cu Erori" : "Facturi Recente"}
            {totalCount > 0 && (
              <span className="text-sm font-normal text-muted-foreground ml-2">
                ({totalCount})
              </span>
            )}
          </h2>
          {isLoading ? (
            <div className="text-center py-12 text-muted-foreground">Se încarcă...</div>
          ) : !invoices.length && page === 0 ? (
            <div className="text-center py-16 border border-dashed border-border rounded-xl">
              <FileText className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
              <p className="text-muted-foreground mb-4">
                {statusFilter ? "Nici o factură în această categorie." : "Nici o factură încă"}
              </p>
              {statusFilter ? (
                <Button variant="outline" onClick={() => setStatusFilter(null)}>
                  Arată toate
                </Button>
              ) : (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button variant="outline" onClick={() => navigate("/invoice/new")}>
                      Creează Prima Factură
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Începe prin a crea prima ta factură</TooltipContent>
                </Tooltip>
              )}
            </div>
          ) : (
            <>
              <div className="space-y-3">
                {invoices.map((inv) => {
                  const sc = statusConfig[inv.status];
                  const Icon = sc.icon;
                  const isSelected = selectedIds.has(inv.id);
                  return (
                    <Tooltip key={inv.id}>
                      <TooltipTrigger asChild>
                        <div
                          className={`flex items-center justify-between rounded-xl border p-4 hover:border-primary/30 transition-colors cursor-pointer ${
                            isSelected ? "border-primary bg-primary/5" : "border-border bg-card"
                          }`}
                          onClick={() => navigate(`/invoice/${inv.id}`)}
                        >
                          <div className="flex items-center gap-3 flex-1 min-w-0">
                            <Checkbox
                              checked={isSelected}
                              onCheckedChange={() => toggleSelect(inv.id)}
                              onClick={(e) => e.stopPropagation()}
                              aria-label={`Selectează factura ${inv.invoice_number || ""}`}
                            />
                            <div className="flex-1 min-w-0">
                              <div className="font-medium truncate">
                                {inv.buyer_name || "Factură nouă"}
                              </div>
                              <div className="text-sm text-muted-foreground">
                                {inv.invoice_number || "Fără număr"} · {new Date(inv.created_at).toLocaleDateString("ro-RO")}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 ml-4">
                            <span className="font-heading font-semibold text-sm">
                              {Number(inv.total).toLocaleString("ro-RO", { style: "currency", currency: "RON" })}
                            </span>
                            <Badge variant={sc.variant} className="gap-1 text-xs">
                              <Icon className="h-3 w-3" />
                              {sc.label}
                            </Badge>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <button
                                  onClick={(e) => handleDownloadPdf(e, inv)}
                                  className="p-1.5 rounded-lg hover:bg-primary/10 text-muted-foreground hover:text-primary transition-colors"
                                >
                                  <Download className="h-4 w-4" />
                                </button>
                              </TooltipTrigger>
                              <TooltipContent>Descarcă PDF</TooltipContent>
                            </Tooltip>
                          </div>
                        </div>
                      </TooltipTrigger>
                      <TooltipContent>Click pentru a vedea detaliile facturii</TooltipContent>
                    </Tooltip>
                  );
                })}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-6 pt-4 border-t border-border">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page === 0}
                    onClick={() => setPage(p => p - 1)}
                    className="gap-1"
                  >
                    <ChevronLeft className="h-4 w-4" />
                    Anterior
                  </Button>
                  <span className="text-sm text-muted-foreground">
                    Pagina <span className="font-semibold text-foreground">{page + 1}</span> din <span className="font-semibold text-foreground">{totalPages}</span>
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page >= totalPages - 1}
                    onClick={() => setPage(p => p + 1)}
                    className="gap-1"
                  >
                    Următor
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              )}
            </>
          )}
      </main>

      {/* Floating bulk-action bar */}
      {selectedIds.size > 0 && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-40 rounded-2xl border border-primary/30 bg-card shadow-lg px-4 py-3 flex items-center gap-3">
          <span className="text-sm font-medium">
            {selectedIds.size} {selectedIds.size === 1 ? "factură selectată" : "facturi selectate"}
          </span>
          <Button size="sm" onClick={() => setAcctDialogOpen(true)}>
            <Mail className="h-4 w-4 mr-1.5" /> Trimite la contabil
          </Button>
          <button
            onClick={clearSelection}
            className="p-1 rounded-lg hover:bg-muted text-muted-foreground"
            aria-label="Anulează selecția"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      <AccountantSendDialog
        open={acctDialogOpen}
        onOpenChange={setAcctDialogOpen}
        invoiceIds={Array.from(selectedIds)}
        contextLabel={`${selectedIds.size} ${selectedIds.size === 1 ? "factură selectată" : "facturi selectate"}`}
        onSent={clearSelection}
      />

      <AppFooter />
    </div>
  );
};

export default Dashboard;
